import {
	COLORS,
	GRID_QUAD,
	SIZE_MATRIX,
	SQUARE_ROTATION,
	STATUSES,
	TABLE,
	TableStatusType,
} from '../constants';
import TableSquare from './tableSquare';
import ImageStatus from './imageStatus';
import TableQuad from './tableQuad';
import TableCircle from './tableCircle';
import { Rect } from 'konva/lib/shapes/Rect';
import Controls from '../controls';
import { DiagramState, icon, position } from '../types';
import { LayoutItem } from 'entities/hallmap/model/types';
import { rotateAroundCenter } from '../lib/utils';
import TableRect from './tableRect';
import { Order } from 'entities/orders';
import { isPast } from 'date-fns';
import { formatTime } from 'shared/lib/time';

export class Table {
	options: any = {
		id: 0,
		form: '',
		size: '',
		name: '',
		guestName: '',
		capacity: 1,
		booked: 0,
		state: 'free',
		notes: ['note', 'note'],
		config: {},
		layout: null,
	};
	status: TableStatusType = 'free';
	isActive = false;
	isSelectable = true;
	isSelected = false;
	layout: LayoutItem | null = null;
	canSelect = true;
	angle = 0;
	id = 0;
	name: string = '';
	order: Order | null = null;

	zoneHighlight: Rect | undefined;
	element: any;
	shape: any;
	imageStatus: ImageStatus | undefined;
	table: any;
	controlsRef: Controls;
	draggable = true;
	disableClick = false;
	onClick: ((arg: any) => void) | undefined;
	onDblClick: ((arg: any) => void) | undefined;
	onUpdate: ((arg: any) => undefined) | undefined;
	onBeforeSelect?: () => void;
	onSelect: ((table: Table, selected: boolean, manual: boolean) => void) | undefined;
	onDeselectTable: ((table: Table) => void) | undefined;
	onConflictTable: ((table: Table, resolved: boolean) => void) | undefined;

	constructor(options = {}, controlsRef: Controls) {
		this.options = options;
		this.controlsRef = controlsRef;
		this.validate();
		this.init();
		this.initOptionProperties();
		this.initImageStatus();
		this.setStatus(this.options.status);
		this.setGuestName(this.options.guestName);
		this.setSelectable(this.isSelectable);
		this.setLayout(this.options.layout);
		this.snapToGrid();
		this.setDiagram(DiagramState.hide, 0);
	}

	private validate() {
		let form;
		let size;
		let capacity;

		// form
		try {
			form = this.options.form.toUpperCase();
		} catch {
			console.warn(
				'table has invalid form type, replaced on square form',
			);
			form = TABLE.FORMS.SQUARE.toUpperCase();
			this.options.form = form.toLowerCase();
		}
		if (!TABLE.FORMS.hasOwnProperty(form)) {
			console.warn(
				'table has invalid form type, replaced on square form',
			);
			form = TABLE.FORMS.SQUARE.toUpperCase();
			this.options.form = form.toLowerCase();
		}

		// size
		try {
			size = this.options.size.toUpperCase();
		} catch {
			console.warn(
				'table has invalid size type, replaced on m size as fallback',
			);
			size = TABLE.SIZES.M.toUpperCase();
			this.options.size = form.toLowerCase();
		}
		if (!TABLE.SIZES.hasOwnProperty(size)) {
			console.warn(
				'table has invalid size type, replaced on m size as fallback',
			);
			size = TABLE.SIZES.M.toUpperCase();
			this.options.size = form.toLowerCase();
		}

		// capacity
		if (!this.options.capacity) {
			capacity = SIZE_MATRIX[`${form}`][size.toUpperCase()][0];
			this.options.capacity = capacity;
			console.warn(
				`table has invalid capacity number, replaced on ${capacity}`,
			);
		}

		let checkCapacity = SIZE_MATRIX[`${form}`][size.toUpperCase()].some(
			(v: any) => v == this.options.capacity,
		);

		if (!checkCapacity) {
			capacity = SIZE_MATRIX[`${form}`][size.toUpperCase()][0];
			this.options.capacity = capacity;
			console.warn(
				`table has invalid capacity number, replaced on ${capacity}`,
			);
		}
	}

	private initOptionProperties() {
		this.id = this.options.id || this.shape._id;
		this.name = this.options.name;
	}

	private init() {
		const { options } = this;
		let table: any;

		if (options.form == TABLE.FORMS.SQUARE) {
			table = new TableSquare(options);
		}
		if (options.form == TABLE.FORMS.QUAD) {
			table = new TableQuad(options);
		}
		if (options.form == TABLE.FORMS.CIRCLE) {
			table = new TableCircle(options);
		}
		if (options.form == TABLE.FORMS.RECT) {
			if(options.capacity == 10 && options.size == TABLE.SIZES.M){
				table = new TableSquare(options);
			} else {
				table = new TableRect(options);
			}
		}

		this.element = table.element;
		this.element.table = this;
		this.shape = table.shape;
		this.table = table;

		this.element.on('xChange', () => {
			this.controlsRef.checkBoundings(this.element.x(), this.element.y(), this.getSize());
		});
		this.element.on('yChange', () => {
			this.controlsRef.checkBoundings(this.element.x(), this.element.y(), this.getSize());
		});

		if (this.options.hasOwnProperty('isSelectable')) {
			this.isSelectable = this.options.isSelectable;
		}
		if (this.options.hasOwnProperty('isActive')) {
			this.isActive = this.options.isActive;
		}

		this.dragsControl();
	}

	private dragsControl() {
		const self = this;

		this.element.on('dragstart', (_: any) => {
			if (this.draggable == false) {
				this.element.stopDrag();
			}
		});

		this.element.on('dragend', (_: any) => {
			// bBox optimization
			this.controlsRef.checkBoundings(this.element.x(), this.element.y(), this.getSize());

			if (self.onUpdate) {
				self.onUpdate(self);
			}
		});

		function singleClick() {
			if (self.onClick) {
				self.onClick(self);
			}
		}
		
		function doubleClick() {
			if (self.onDblClick) {
				self.onDblClick(self);
			}
		}

		let timeout: any = null;

		this.element.on('click', (clickEvent: any) => {	
			if (window.matchMedia("(pointer: coarse)").matches) {
				return;
			}

			if (self.canSelect) {
				self.select(true, true);
			}

			if (clickEvent.evt.detail === 1) {
				timeout = setTimeout(() => {
					singleClick();
				}, 300);
				return;
			}

			if (clickEvent.evt.detail === 2) {
				if (timeout) {
					clearTimeout(timeout);
				}

				doubleClick();
				return;
			}
		});

		let start = 0;
		let end = 0;
		var touchDuration = 500;

		this.element.on('touchstart', (clickEvent: any) => {	
			start = clickEvent.evt.timeStamp;
		});

		this.element.on('touchend', (clickEvent: any) => {	
			end = clickEvent.evt.timeStamp;

			const diff = end - start;

			if (self.canSelect) {
				self.select(true, true);
			}

			if (diff < touchDuration) {
				singleClick();
			} else {
				doubleClick();
			}
		});
	}

	private initImageStatus() {
		const options = {
			booked: this.options.booked,
			name: this.options.name,
			state: this.options.state,
			guestName: this.options.guestName,
		};
		const imageStatus = new ImageStatus(options, this);
		this.imageStatus = imageStatus;

		this.element.add(imageStatus.element);
	}

	private snapToGrid() {
		this.element.on('dragend', (_: any) => {
			this.element.position({
				x: Math.round(this.element.x() / GRID_QUAD) * GRID_QUAD,
				y: Math.round(this.element.y() / GRID_QUAD) * GRID_QUAD,
			});
		});
	}

	setIcon(iconAsset: icon) {
		if (this.imageStatus) {
			this.imageStatus.setIcon(iconAsset);
		}
	}

	private getSize(){
		const isCircle = this.shape.constructor.name == 'Circle';
		const size = {width: 0, height: 0};
		const shape = this.shape

		if(isCircle){
			size.width = shape.radius() * 2;
			size.height = shape.radius() * 2;
        } else {
			size.width = shape.width();
			size.height = shape.height();
        }

		return size;
	}

	setDisableClick(isDisable: boolean) {
		this.disableClick = isDisable;
	}

	getPosition(): position {
		return {
			x: Math.round(this.element.x() / GRID_QUAD) * GRID_QUAD,
			y: Math.round(this.element.y() / GRID_QUAD) * GRID_QUAD,
		};
	}
	rotate(rType: string | number) {
		let angle: any = 0;
		
		if(rType == SQUARE_ROTATION.HORIZONTAL){
			angle = 0;
		}
		else if(rType == SQUARE_ROTATION.VERTICAL){
			angle = 90;
        } else {
			angle = rType;
		}
		
		this.angle = angle

		const tableElement = this.table.element;
		const seats = tableElement.find('.seats')[0];
		const shape = tableElement.children[1];

		if(shape.constructor.name != "Circle"){
			rotateAroundCenter(shape, angle);
		}
		
		rotateAroundCenter(seats, angle, shape);

		
	}

	changeAppearence(
		bgColor: string,
		borderColor?: string,
		seatsColor?: string | false,
		opacity?: number,
	) {
		const resetToDefaults = () => {
			this.shape.opacity(1);
			this.table.seats.children.forEach((seat: any) => {
				seat.opacity(1);
				this.shape.fill('white');
			});
		};

		resetToDefaults();

		if (bgColor) {
			this.shape.fill(bgColor);
		}

		if (borderColor) {
			this.shape.stroke(borderColor);
		}

		if (seatsColor) {
			this.table.seats.children.forEach((seat: any) => {
				seat.fill(seatsColor);
			});
		}

		if (opacity) {
			this.shape.opacity(opacity);
			this.table.seats.children.forEach((seat: any) => {
				seat.opacity(opacity);
			});
		}
	}

	select(selected: boolean = false, manual: boolean = false, callback: boolean = true) {
		if (this.disableClick) {
			return;
		}

		if (this.onBeforeSelect && manual) {
			this.onBeforeSelect();
		}

		this.isSelected = !this.isSelected;
		if (this.isSelected && this.isActive) {
			this.shape.strokeWidth(4);
			this.shape.stroke(COLORS.SELECTED);

			if (this.onSelect&&callback) {
				this.onSelect(this, selected, manual);
			}
		} else {
			this.deselect();
		}
	}

	deselect() {
		this.isSelected = false;
		this.shape.strokeWidth(1);
		this.shape.stroke(COLORS.BORDER);

		if (this.onDeselectTable) {
			this.onDeselectTable(this);
		}

		if (!this.isActive) {
			this.setInactiveAppearence();
			return;
		}
	}

	setBooked(number: number, color?: string) {
		if (this.imageStatus) {
			this.imageStatus.setBooked(number, color);
		}
	}

	setTimeLabel(text: string, color?: string) {
		if (this.imageStatus) {
			this.imageStatus.setTimeLabel(text, color);
		}
	}

	setName(name: string) {
		if (this.imageStatus) {
			this.imageStatus.setName(name);
		}
	}

	setGuestName(name: string) {
		if (this.imageStatus) {
			this.imageStatus.setGuestName(name);
		}
	}

	setOrder(order: Order) {
		this.order = order;
	}

	setInactiveAppearence() {
		let colorBorder = COLORS.SEATS.INACTIVE;

		if (this.isSelected) {
			colorBorder = COLORS.SELECTED_CONFLICT;
			if (this.onConflictTable) {
				this.onConflictTable(this, false);
			}
		}

		this.changeAppearence(
			COLORS.SEATS.INACTIVE,
			colorBorder,
			COLORS.SEATS.INACTIVE,
			0.5,
		);
		this.setIcon('');
		this.setBooked(0);
	}

	setActiveAppearence() {
		let colorBorder = COLORS.BORDER;
		if (this.isSelected) {
			colorBorder = COLORS.SELECTED;
			if (this.onConflictTable) {
				this.onConflictTable(this, true);
			}
		}

		this.changeAppearence(
			COLORS.TABLE.FREE.BG,
			colorBorder,
			COLORS.SEATS.ACTIVE,
		);
		this.setIcon('');
		this.setBooked(0);
	}

	setActive(state: boolean) {
		this.isActive = state;
		if (state == false) {
			this.setInactiveAppearence();
		} else {
			this.setActiveAppearence();
		}
	}

	setSelectable(state: boolean) {
		this.isSelectable = state;
	}

	setLayout(layout: LayoutItem) {
		this.layout = layout;
	}

	setDiagramColors(arcFront: string, arcBack: string): void{
		this.imageStatus?.setDiagramColors(arcFront, arcBack);
	}

	setDiagram(state: number, progress: number) {
		this.imageStatus?.setDiagram(state, progress);
	}

	setStatus(status: TableStatusType) {
		this.status = status;

		this.setIcon('');

		if (this.isActive == false) {
			return;
		}

		if (status == STATUSES.FREE) {
			this.changeAppearence(
				COLORS.TABLE.FREE.BG,
				this.isSelected ? COLORS.SELECTED : COLORS.BORDER,
				COLORS.SEATS.ACTIVE,
			);
		}

		if (status == STATUSES.WAITING || status === STATUSES.DELAY) {
			if (this.order) {
				const date = formatTime({ time: this.order.dt_start });
				if (!isPast(this.order.dt_start)) {
					this.changeAppearence(
						COLORS.TABLE.WAITING.BG,
						this.isSelected ? COLORS.SELECTED : COLORS.BORDER,
						COLORS.SEATS.ACTIVE,
					);
					if (date) {
						this.setTimeLabel(date?.time)
					}
					this.setIcon(`${window.location.origin}/hallmap/icons/person.svg`);
				} else {
					this.changeAppearence(
						COLORS.TABLE.DELAY.BG,
						this.isSelected ? COLORS.SELECTED : COLORS.BORDER,
						COLORS.SEATS.ACTIVE,
					);
					this.setIcon(`${window.location.origin}/hallmap/icons/person-white.svg`);
					if (date) {
						this.setTimeLabel(date?.time, 'white')
					}
				}
			}
		}
		if (status == STATUSES.BUSY) {
			this.changeAppearence(
				COLORS.TABLE.BUSY.BG,
				this.isSelected ? COLORS.SELECTED : COLORS.BORDER,
				COLORS.SEATS.ACTIVE,
			);
		}

		if (status === STATUSES.ENDING) {
			this.setIcon(`${window.location.origin}/hallmap/icons/timer.svg`);
		}

		if (status === STATUSES.EXCEEDING || status == STATUSES.ENDING) {
			this.changeAppearence(
				COLORS.TABLE.EXCEEDING.BG,
				this.isSelected ? COLORS.SELECTED : COLORS.BORDER,
				COLORS.SEATS.ACTIVE,
			);
		}

		if (status === STATUSES.RELEVANT) {
			this.changeAppearence(
				COLORS.TABLE.RELEVANT.BG,
				this.isSelected ? COLORS.SELECTED : COLORS.BORDER,
				COLORS.SEATS.ACTIVE,
			);
		}
	}
}

export default Table;
